import axiosInstance from "../Interceptors/axiosInstance.jsx";
import { fetchRoles } from "./roleService";

const jsonHeaders = {
  accept: "*/*",
  "Content-Type": "application/json",
};

const multipartHeaders = {
  "Content-Type": "multipart/form-data",
};

const buildRoleNameMap = (roles = []) =>
  roles.reduce((map, role) => {
    const roleId = String(role.id ?? role.Id ?? "");
    const roleName = role.roleName ?? role.RoleName ?? "";
    if (roleId) {
      map[roleId] = roleName;
    }
    return map;
  }, {});

export const normalizeUserListItem = (user, roleNameMap = {}) => {
  if (!user || typeof user !== "object") {
    return user;
  }

  const userRole = user.userRole ?? user.UserRole ?? "";
  const roleName =
    user.roleName ??
    user.RoleName ??
    roleNameMap[String(userRole)] ??
    "";

  return {
    ...user,
    id: user.id ?? user.Id,
    userId: user.userId ?? user.UserId ?? "",
    userName: user.userName ?? user.UserName ?? "",
    emailId: user.emailId ?? user.EmailId ?? "",
    contactNo: user.contactNo ?? user.ContactNo ?? "",
    profileImage: user.profileImage ?? user.ProfileImage ?? "",
    registeredOn: user.registeredOn ?? user.RegisteredOn ?? "",
    userRole,
    roleName,
  };
};

const extractUserListResult = (response) => {
  const result = response?.data?.result;

  if (Array.isArray(result)) {
    return result;
  }

  if (Array.isArray(result?.items)) {
    return result.items;
  }

  if (Array.isArray(result?.users)) {
    return result.users;
  }

  return [];
};

const extractUserListTotal = (response, users) => {
  const result = response?.data?.result;

  if (typeof result?.totalCount === "number") {
    return result.totalCount;
  }

  if (typeof result?.total === "number") {
    return result.total;
  }

  return users[0]?.totalCount ?? users.length;
};

const enrichUsersWithRoleNames = async (users) => {
  const needsRoleLookup = users.some(
    (user) =>
      !(user.roleName ?? user.RoleName) &&
      (user.userRole ?? user.UserRole) !== undefined &&
      (user.userRole ?? user.UserRole) !== null &&
      String(user.userRole ?? user.UserRole) !== ""
  );

  if (!needsRoleLookup) {
    return users.map((user) => normalizeUserListItem(user));
  }

  const roles = await fetchRoles();
  const roleNameMap = buildRoleNameMap(roles);
  return users.map((user) => normalizeUserListItem(user, roleNameMap));
};

export const getUsers = async ({
  pageSize = 10,
  pageNo = 1,
  sParam = "",
  fromDate = "",
  toDate = "",
} = {}) => {
  const response = await axiosInstance.post(
    "access/users",
    {
      pageSize,
      pageNo,
      sParam,
      fromDate,
      toDate,
    },
    { headers: jsonHeaders }
  );

  const users = extractUserListResult(response);
  const normalizedUsers = await enrichUsersWithRoleNames(users);
  const totalCount = extractUserListTotal(response, users);

  return {
    ...response,
    data: {
      ...response.data,
      result: normalizedUsers,
      totalCount,
    },
  };
};

export const addUser = async (ownerData) => {
  const formData = new FormData();
  formData.append("UserName", ownerData.UserName);
  formData.append("UserId", ownerData.UserId);
  formData.append("UserRole", ownerData.UserRole);
  formData.append("EmailId", ownerData.EmailId);
  formData.append("ContactNo", ownerData.ContactNo);
  formData.append("Password", ownerData.owner_password);

  if (ownerData.profileImage) {
    formData.append("ProfileImage", ownerData.profileImage);
  }

  const response = await axiosInstance.post("access/add-user", formData, {
    headers: multipartHeaders,
  });
  return response.data;
};

export const deleteUser = async (userid) => {
  return await axiosInstance.delete(`access/delete-user/${userid}`, {
    headers: jsonHeaders,
  });
};

export const updateUser = async (payload) => {
  return await axiosInstance.put("access/update-user", payload, {
    headers: multipartHeaders,
  });
};

export const fetchUserById = async (userid) => {
  const response = await axiosInstance.get(`access/user/${userid}`, {
    headers: jsonHeaders,
  });
  return response.data.result;
};

export const changePassword = async (data) => {
  return await axiosInstance.put("auth/change-password", data, {
    headers: jsonHeaders,
  });
};

export const createPassword = async (data) => {
  return await axiosInstance.put("auth/create-password", data, {
    headers: jsonHeaders,
  });
};

/** GET /access/my-profile — Bearer token via axios interceptor */
export const fetchUserProfile = async () => {
  return await axiosInstance.get("access/my-profile", { headers: jsonHeaders });
};

export const updateProfilePage = async (payload) => {
  return await axiosInstance.put("access/my-profile", payload, {
    headers: multipartHeaders,
  });
};
